import React, { useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Link } from "react-router-dom";
import loginlogo from "../../../assets/images/login-logo.svg";
import loginimage from "../../../assets/images/login-image.jpg";
import "./takeactionorder.scss";

interface TakeActionOrderFormValues {
  clientId: string;
  username: string;
  password: string;
  orderNumber: string;
  notes: string;
}

const TakeActionOrder: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionResult, setActionResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const formik = useFormik<TakeActionOrderFormValues>({
    initialValues: {
      clientId: "",
      username: "",
      password: "",
      orderNumber: "",
      notes: "",
    },
    validationSchema: Yup.object({
      clientId: Yup.string().required("Client Id is required"),
      username: Yup.string().required("Username is required"),
      password: Yup.string().required("Password is required"),
      orderNumber: Yup.string().required("Order # is required"),
    }),
    onSubmit: () => {},
  });

  const handleAction = async (action: "approve" | "reject") => {
    const errors = await formik.validateForm();
    formik.setTouched({
      clientId: true,
      username: true,
      password: true,
      orderNumber: true,
      notes: true,
    });

    if (Object.keys(errors).length > 0) return;

    setIsSubmitting(true);
    setActionResult(null);

    try {
      // TODO: Replace with actual API call
      // const response = await approveRejectOrderAPI({
      //   clientId: formik.values.clientId,
      //   username: formik.values.username,
      //   password: formik.values.password,
      //   orderNumber: formik.values.orderNumber,
      //   notes: formik.values.notes,
      //   action: action,
      // });

      setActionResult({
        success: true,
        message: `Order ${formik.values.orderNumber} has been ${action === "approve" ? "approved" : "rejected"} successfully.`,
      });
    } catch (error: any) {
      setActionResult({
        success: false,
        message:
          error?.message || "Something went wrong. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-wrapper take-action-wrapper">
      <Container fluid>
        <Row style={{ minHeight: "100vh" }}>
          <Col xs={12} md={6}>
            <div className="login-form-wrapper take-action-form-wrapper">
              <figure>
                <img src={loginlogo} alt="logo" />
              </figure>
              <div className="login-title-wrapper">
                <h4>Approve/Reject Order</h4>
                <p>Please enter your credentials to approve or reject the order.</p>
              </div>

              {actionResult && (
                <div
                  className={`alert ${actionResult.success ? "alert-success" : "alert-danger"} mb-0`}
                  role="alert"
                >
                  {actionResult.message}
                </div>
              )}

              <Form noValidate onSubmit={formik.handleSubmit}>
                <Form.Group className="mb-4">
                  <Form.Label>Client ID</Form.Label>
                  <Form.Control
                    type="text"
                    name="clientId"
                    placeholder="Enter Client ID"
                    value={formik.values.clientId}
                    onChange={(e) => {
                      formik.setFieldValue(
                        "clientId",
                        e.target.value.toUpperCase()
                      );
                    }}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      !!(formik.touched.clientId && formik.errors.clientId)
                    }
                    disabled={isSubmitting}
                  />
                  {formik.touched.clientId && formik.errors.clientId ? (
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.clientId}
                    </Form.Control.Feedback>
                  ) : null}
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Username</Form.Label>
                  <Form.Control
                    type="text"
                    name="username"
                    placeholder="Enter Username"
                    value={formik.values.username}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      !!(formik.touched.username && formik.errors.username)
                    }
                    disabled={isSubmitting}
                  />
                  {formik.touched.username && formik.errors.username ? (
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.username}
                    </Form.Control.Feedback>
                  ) : null}
                </Form.Group>

                <Form.Group className="mb-4 password-field-box">
                  <Form.Label>Password</Form.Label>
                  <Form.Control
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Enter Password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      !!(formik.touched.password && formik.errors.password)
                    }
                    disabled={isSubmitting}
                  />
                  {!formik.errors.password ? (
                    showPassword ? (
                      <i
                        className="bi bi-eye-fill"
                        onClick={() => setShowPassword(!showPassword)}
                      />
                    ) : (
                      <i
                        className="bi bi-eye-slash-fill"
                        onClick={() => setShowPassword(!showPassword)}
                      />
                    )
                  ) : null}
                  {formik.touched.password && formik.errors.password ? (
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.password}
                    </Form.Control.Feedback>
                  ) : null}
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Order #</Form.Label>
                  <Form.Control
                    type="text"
                    name="orderNumber"
                    placeholder="Enter Order Number"
                    value={formik.values.orderNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      !!(
                        formik.touched.orderNumber &&
                        formik.errors.orderNumber
                      )
                    }
                    disabled={isSubmitting}
                  />
                  {formik.touched.orderNumber && formik.errors.orderNumber ? (
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.orderNumber}
                    </Form.Control.Feedback>
                  ) : null}
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="notes"
                    placeholder="Enter Notes (Optional)"
                    value={formik.values.notes}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={isSubmitting}
                  />
                </Form.Group>

                <Form.Group className="d-grid gap-2 d-flex">
                  <Button
                    variant="primary"
                    className="flex-fill"
                    onClick={() => handleAction("approve")}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Processing..." : "Approve"}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    className="flex-fill"
                    onClick={() => handleAction("reject")}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Processing..." : "Reject"}
                  </Button>
                </Form.Group>
              </Form>
              <div className="login-bottom-wrapper">
                <span className="copy-text">
                  Copyright &copy; {new Date().getFullYear()} TriNet Medical,
                  LLC. All rights reserved.
                </span>
              </div>
            </div>
          </Col>
          <Col
            style={{
              backgroundImage: `url(${loginimage})`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center center",
              backgroundSize: "cover",
            }}
            xs={12}
            md={6}
          >
            <div className="login-image-wrapper">
              <h4>Company</h4>
              <h1>Overview</h1>
              <p>
                TriNet Medical provides Healthcare IT solutions with
                quantifiable results in the areas of Data Management services,
                Technology Services and Barcode Integration with EMR.
              </p>
              <p>
                We deliver highly valuable and innovative solutions that bridge
                our client's execution gaps through a clear understanding of
                their business needs and a culture of shared commitments.
              </p>
              <div className="url-link-box">
                <Link
                  className="text-primary-700"
                  to="https://www.trinetmedical.com/"
                  target="_blank"
                >
                  www.trinetmedical.com
                </Link>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default TakeActionOrder;
